// skills/husbandry/husbandryData.js
// Pet definitions, contracts, and evolution data for Husbandry skill

import { HUSBANDRY_CONFIG, PET_CONFIG } from '../../game/constants.js';

// Re-export config for convenience
export { HUSBANDRY_CONFIG, PET_CONFIG };

/**
 * Pet definitions with roles, evolutions, and unlock requirements
 */
export const PETS = {
    ROOTLING: {
        id: 'rootling',
        name: 'Rootling',
        evolutions: ['Rootling', 'Rootling Scout', 'Rootling Elder'],
        role: 'gatherer',
        unlockLevel: HUSBANDRY_CONFIG.UNLOCK_LEVELS.rootling,
        contractType: 'resource',
        xpSkills: ['gatherer', 'husbandry'],
        description: 'A forest spirit that excels at gathering resources.',
        icon: '🌱',
        color: '#7cba5f'
    },
    FELLTRACKER: {
        id: 'felltracker',
        name: 'Felltracker',
        evolutions: ['Felltracker', 'Fellhunter', 'Fellstalker'],
        role: 'combat',
        unlockLevel: HUSBANDRY_CONFIG.UNLOCK_LEVELS.felltracker,
        contractType: 'combat',
        xpSkills: ['bounty', 'husbandry'],
        description: 'A predatory beast trained for combat missions.',
        icon: '🐺',
        color: '#8b4513'
    },
    ICHOREBOUND: {
        id: 'ichorebound',
        name: 'Ichorebound',
        evolutions: ['Ichorebound', 'Ichorewarden', 'Ichoremaster'],
        role: 'purifier',
        unlockLevel: HUSBANDRY_CONFIG.UNLOCK_LEVELS.ichorebound,
        contractType: 'purification',
        xpSkills: ['husbandry'],
        description: 'An entity bound to arcane ichor, purifying corruption.',
        icon: '🔮',
        color: '#9932cc'
    },
    CHIMERA: {
        id: 'chimera',
        name: 'Chimera',
        evolutions: ['Chimera', 'Chimera Vanguard', 'Chimera Apex'],
        role: 'hybrid',
        unlockLevel: HUSBANDRY_CONFIG.UNLOCK_LEVELS.chimera,
        contractType: 'all',
        xpSkills: ['dynamic'], // XP depends on contract type
        description: 'A versatile hybrid creature adapting to any task.',
        icon: '🦁',
        color: '#daa520'
    },
    EIDOLON: {
        id: 'eidolon',
        name: 'Eidolon',
        evolutions: ['Eidolon', 'Eidolon Ascendant', 'Eidolon Primarch'],
        role: 'elite',
        unlockLevel: HUSBANDRY_CONFIG.UNLOCK_LEVELS.eidolon,
        questUnlock: 'pet_eidolon',
        contractType: 'all',
        xpSkills: ['gatherer', 'bounty', 'husbandry'],
        description: 'An ancient spirit of immense power.',
        icon: '👻',
        color: '#4b0082'
    }
};

/**
 * Pet contracts - missions pets can undertake
 * Rewards use existing item IDs from the item registry
 *
 * Insight drop chances by pet tier (conservative for regular pets, higher for Eidolon):
 * - Rootling: 2%/4%/6% (short/medium/long) - Faint Insight only
 * - Felltracker: 3%/5%/8% - Faint or Smoldering
 * - Ichorebound: 3%/5%/8% - Smoldering or Kindled
 * - Chimera: 4%/7%/10% - Any up to Blazing
 * - Eidolon (normal): 6%/10%/15% - Smoldering to Blazing
 * - Eidolon (veil): 10%/18%/25% - Blazing or Veil-Touched
 */
export const PET_CONTRACTS = {
    // ==================== RESOURCE CONTRACTS (Gatherer XP) ====================
    // Rootling specialty - nature/gathering focus
    // Thematic items: seeds, herbs, crops, timber, pet materials
    BASIC_FORAGE: {
        id: 'basic_forage',
        name: 'Basic Forage',
        duration: PET_CONFIG.CONTRACT_DURATIONS.short, // 45 min
        xp: 100,
        type: 'resource',
        description: 'A short foraging expedition.',
        rewards: [
            { itemId: 'twisted_root_fiber', minQty: 3, maxQty: 6, chance: 0.9 },
            { itemId: 'common_timber', minQty: 5, maxQty: 10, chance: 0.7 },
            { itemId: 'potato_seed', minQty: 1, maxQty: 3, chance: 0.4 },
            { itemId: 'grimy_greenleaf', minQty: 2, maxQty: 4, chance: 0.35 }
        ],
        insightChance: { rootling: 0.02, felltracker: 0, ichorebound: 0, chimera: 0.04, eidolon: 0.06 },
        insightTiers: { rootling: ['faint_insight'], chimera: ['faint_insight', 'smoldering_insight'], eidolon: ['smoldering_insight', 'kindled_insight'] }
    },
    EXTENDED_FORAGE: {
        id: 'extended_forage',
        name: 'Extended Forage',
        duration: PET_CONFIG.CONTRACT_DURATIONS.medium, // 90 min
        xp: 250,
        type: 'resource',
        description: 'A longer gathering expedition with better rewards.',
        rewards: [
            { itemId: 'twisted_root_fiber', minQty: 5, maxQty: 10, chance: 1.0 },
            { itemId: 'ironwood_logs', minQty: 8, maxQty: 15, chance: 0.8 },
            { itemId: 'living_arcane_husk', minQty: 2, maxQty: 5, chance: 0.5 },
            { itemId: 'cabbage_seed', minQty: 1, maxQty: 3, chance: 0.4 },
            { itemId: 'greenleaf_seed', minQty: 1, maxQty: 2, chance: 0.35 },
            { itemId: 'grimy_duskleaf', minQty: 2, maxQty: 5, chance: 0.4 }
        ],
        insightChance: { rootling: 0.04, felltracker: 0, ichorebound: 0, chimera: 0.07, eidolon: 0.10 },
        insightTiers: { rootling: ['faint_insight'], chimera: ['faint_insight', 'smoldering_insight'], eidolon: ['smoldering_insight', 'kindled_insight'] }
    },
    DEEP_EXPEDITION: {
        id: 'deep_expedition',
        name: 'Deep Expedition',
        duration: PET_CONFIG.CONTRACT_DURATIONS.long, // 2 hours
        xp: 500,
        type: 'resource',
        description: 'A dangerous expedition into the wilds.',
        rewards: [
            { itemId: 'twisted_root_fiber', minQty: 8, maxQty: 15, chance: 1.0 },
            { itemId: 'living_arcane_husk', minQty: 5, maxQty: 10, chance: 0.8 },
            { itemId: 'condensed_arcane_core_fragment', minQty: 2, maxQty: 4, chance: 0.4 },
            { itemId: 'tomato_seed', minQty: 1, maxQty: 3, chance: 0.4 },
            { itemId: 'ironbloom_seed', minQty: 1, maxQty: 2, chance: 0.3 },
            { itemId: 'grimy_ironbloom', minQty: 3, maxQty: 6, chance: 0.45 },
            { itemId: 'potato', minQty: 5, maxQty: 10, chance: 0.5 }
        ],
        insightChance: { rootling: 0.06, felltracker: 0, ichorebound: 0, chimera: 0.10, eidolon: 0.15 },
        insightTiers: { rootling: ['faint_insight'], chimera: ['faint_insight', 'smoldering_insight', 'kindled_insight'], eidolon: ['kindled_insight', 'blazing_insight'] }
    },

    // ==================== COMBAT CONTRACTS (Bounty XP) ====================
    // Felltracker specialty - hunting/combat focus
    // Thematic items: fish, hides, bones, ashes, arrow materials
    PATROL_DUTY: {
        id: 'patrol_duty',
        name: 'Patrol Duty',
        duration: PET_CONFIG.CONTRACT_DURATIONS.short,
        xp: 120,
        type: 'combat',
        description: 'Guard the perimeter and fend off threats.',
        rewards: [
            { itemId: 'raw_sand_smelt', minQty: 5, maxQty: 10, chance: 0.85 },
            { itemId: 'treated_hide', minQty: 3, maxQty: 6, chance: 0.9 },
            { itemId: 'ashes', minQty: 5, maxQty: 12, chance: 0.7 },
            { itemId: 'small_bone', minQty: 2, maxQty: 5, chance: 0.5 },
            { itemId: 'arrow_shafts', minQty: 5, maxQty: 10, chance: 0.4 }
        ],
        insightChance: { rootling: 0, felltracker: 0.03, ichorebound: 0, chimera: 0.04, eidolon: 0.06 },
        insightTiers: { felltracker: ['faint_insight', 'smoldering_insight'], chimera: ['faint_insight', 'smoldering_insight'], eidolon: ['smoldering_insight', 'kindled_insight'] }
    },
    HUNT_MISSION: {
        id: 'hunt_mission',
        name: 'Hunt Mission',
        duration: PET_CONFIG.CONTRACT_DURATIONS.medium,
        xp: 300,
        type: 'combat',
        description: 'Track and hunt dangerous creatures.',
        rewards: [
            { itemId: 'raw_river_char', minQty: 8, maxQty: 15, chance: 0.9 },
            { itemId: 'green_wyrm_hide', minQty: 4, maxQty: 8, chance: 0.85 },
            { itemId: 'blue_wyrm_hide', minQty: 2, maxQty: 4, chance: 0.5 },
            { itemId: 'spun_fiber', minQty: 3, maxQty: 6, chance: 0.6 },
            { itemId: 'ashes', minQty: 8, maxQty: 16, chance: 0.75 },
            { itemId: 'charred_bone', minQty: 2, maxQty: 5, chance: 0.5 },
            { itemId: 'arrow_shafts', minQty: 8, maxQty: 15, chance: 0.45 },
            { itemId: 'pyrestone_arrowheads', minQty: 3, maxQty: 8, chance: 0.35 }
        ],
        insightChance: { rootling: 0, felltracker: 0.05, ichorebound: 0, chimera: 0.07, eidolon: 0.10 },
        insightTiers: { felltracker: ['faint_insight', 'smoldering_insight'], chimera: ['smoldering_insight', 'kindled_insight'], eidolon: ['kindled_insight', 'blazing_insight'] }
    },
    BOUNTY_HUNT: {
        id: 'bounty_hunt',
        name: 'Bounty Hunt',
        duration: PET_CONFIG.CONTRACT_DURATIONS.long,
        xp: 600,
        type: 'combat',
        description: 'Hunt a dangerous target for valuable rewards.',
        rewards: [
            { itemId: 'raw_spiny_lobster', minQty: 10, maxQty: 20, chance: 0.95 },
            { itemId: 'raw_mako', minQty: 3, maxQty: 6, chance: 0.4 },
            { itemId: 'red_wyrm_hide', minQty: 5, maxQty: 10, chance: 0.8 },
            { itemId: 'black_drake_hide', minQty: 2, maxQty: 5, chance: 0.5 },
            { itemId: 'spun_fiber', minQty: 5, maxQty: 10, chance: 0.6 },
            { itemId: 'dragon_bone', minQty: 2, maxQty: 4, chance: 0.45 },
            { itemId: 'arrow_shafts', minQty: 10, maxQty: 20, chance: 0.5 },
            { itemId: 'blackiron_arrowheads', minQty: 3, maxQty: 8, chance: 0.35 }
        ],
        insightChance: { rootling: 0, felltracker: 0.08, ichorebound: 0, chimera: 0.10, eidolon: 0.15 },
        insightTiers: { felltracker: ['smoldering_insight', 'kindled_insight'], chimera: ['kindled_insight', 'blazing_insight'], eidolon: ['kindled_insight', 'blazing_insight'] }
    },

    // ==================== PURIFICATION CONTRACTS (Husbandry XP) ====================
    // Ichorebound specialty - arcane/purifier focus
    // Thematic items: ichor, clean herbs, glyphs, hides
    CLEANSE_GROVE: {
        id: 'cleanse_grove',
        name: 'Cleanse Grove',
        duration: PET_CONFIG.CONTRACT_DURATIONS.short,
        xp: 150,
        type: 'purification',
        description: 'Purify a corrupted grove.',
        rewards: [
            { itemId: 'arcane_residue', minQty: 5, maxQty: 10, chance: 0.9 },
            { itemId: 'twisted_root_fiber', minQty: 3, maxQty: 6, chance: 0.6 },
            { itemId: 'treated_hide', minQty: 3, maxQty: 5, chance: 0.7 },
            { itemId: 'clean_greenleaf', minQty: 2, maxQty: 4, chance: 0.4 },
            { itemId: 'storm_glyph', minQty: 1, maxQty: 1, chance: 0.15 }
        ],
        insightChance: { rootling: 0, felltracker: 0, ichorebound: 0.03, chimera: 0.04, eidolon: 0.06 },
        insightTiers: { ichorebound: ['smoldering_insight', 'kindled_insight'], chimera: ['smoldering_insight', 'kindled_insight'], eidolon: ['kindled_insight', 'blazing_insight'] }
    },
    SANCTIFY_RUINS: {
        id: 'sanctify_ruins',
        name: 'Sanctify Ruins',
        duration: PET_CONFIG.CONTRACT_DURATIONS.medium,
        xp: 350,
        type: 'purification',
        description: 'Cleanse ancient ruins of dark energy.',
        rewards: [
            { itemId: 'arcane_residue', minQty: 8, maxQty: 15, chance: 0.95 },
            { itemId: 'refined_arcane_ichor', minQty: 3, maxQty: 6, chance: 0.6 },
            { itemId: 'blue_wyrm_hide', minQty: 3, maxQty: 6, chance: 0.7 },
            { itemId: 'clean_duskleaf', minQty: 2, maxQty: 5, chance: 0.45 },
            { itemId: 'clean_ironbloom', minQty: 1, maxQty: 3, chance: 0.3 },
            { itemId: 'frost_glyph', minQty: 1, maxQty: 1, chance: 0.2 },
            { itemId: 'stone_glyph', minQty: 1, maxQty: 1, chance: 0.15 }
        ],
        insightChance: { rootling: 0, felltracker: 0, ichorebound: 0.05, chimera: 0.07, eidolon: 0.10 },
        insightTiers: { ichorebound: ['smoldering_insight', 'kindled_insight'], chimera: ['kindled_insight', 'blazing_insight'], eidolon: ['kindled_insight', 'blazing_insight'] }
    },
    VOID_PURGE: {
        id: 'void_purge',
        name: 'Void Purge',
        duration: PET_CONFIG.CONTRACT_DURATIONS.long,
        xp: 700,
        type: 'purification',
        description: 'Purge a void breach of its corruption.',
        rewards: [
            { itemId: 'refined_arcane_ichor', minQty: 5, maxQty: 10, chance: 0.95 },
            { itemId: 'purified_arcane_core', minQty: 2, maxQty: 4, chance: 0.6 },
            { itemId: 'living_arcane_husk', minQty: 4, maxQty: 8, chance: 0.7 },
            { itemId: 'void_drake_hide', minQty: 3, maxQty: 6, chance: 0.65 },
            { itemId: 'clean_emberroot', minQty: 2, maxQty: 4, chance: 0.4 },
            { itemId: 'flame_glyph', minQty: 1, maxQty: 2, chance: 0.25 },
            { itemId: 'vigor_glyph', minQty: 1, maxQty: 1, chance: 0.15 }
        ],
        insightChance: { rootling: 0, felltracker: 0, ichorebound: 0.08, chimera: 0.10, eidolon: 0.15 },
        insightTiers: { ichorebound: ['kindled_insight', 'blazing_insight'], chimera: ['kindled_insight', 'blazing_insight'], eidolon: ['blazing_insight'] }
    },

    // ==================== VEIL EXPEDITION CONTRACTS (Eidolon Exclusive) ====================
    // Only Eidolon can undertake these missions beyond the Pyre Veil
    // High-tier rewards including guaranteed Veil Embers and best insight chances
    VEIL_SCOUTING: {
        id: 'veil_scouting',
        name: 'Veil Scouting',
        duration: PET_CONFIG.CONTRACT_DURATIONS.short,
        xp: 200,
        type: 'veil',
        description: 'Scout the edges of the Veil, gathering what drifts through.',
        eidolonOnly: true,
        rewards: [
            { itemId: 'veil_ember', minQty: 2, maxQty: 4, chance: 1.0 },
            { itemId: 'refined_arcane_ichor', minQty: 3, maxQty: 6, chance: 0.8 },
            { itemId: 'red_wyrm_hide', minQty: 1, maxQty: 2, chance: 0.4 },
            { itemId: 'void_glyph', minQty: 1, maxQty: 1, chance: 0.2 },
            { itemId: 'binding_glyph', minQty: 1, maxQty: 1, chance: 0.15 }
        ],
        insightChance: { eidolon: 0.10 },
        insightTiers: { eidolon: ['blazing_insight', 'veil_touched_insight'] }
    },
    SPECTRAL_HUNT: {
        id: 'spectral_hunt',
        name: 'Spectral Hunt',
        duration: PET_CONFIG.CONTRACT_DURATIONS.medium,
        xp: 450,
        type: 'veil',
        description: 'Hunt spectral entities within the Veil\'s domain.',
        eidolonOnly: true,
        rewards: [
            { itemId: 'veil_ember', minQty: 4, maxQty: 8, chance: 1.0 },
            { itemId: 'refined_arcane_ichor', minQty: 5, maxQty: 10, chance: 0.9 },
            { itemId: 'purified_arcane_core', minQty: 2, maxQty: 4, chance: 0.5 },
            { itemId: 'black_drake_hide', minQty: 2, maxQty: 4, chance: 0.6 },
            { itemId: 'doom_glyph', minQty: 1, maxQty: 1, chance: 0.25 },
            { itemId: 'sanguine_glyph', minQty: 1, maxQty: 1, chance: 0.2 }
        ],
        insightChance: { eidolon: 0.18 },
        insightTiers: { eidolon: ['blazing_insight', 'veil_touched_insight'] }
    },
    CONVERGENCE_DIVE: {
        id: 'convergence_dive',
        name: 'Convergence Dive',
        duration: PET_CONFIG.CONTRACT_DURATIONS.long,
        xp: 900,
        type: 'veil',
        description: 'Venture deep into the Convergence where all flames return.',
        eidolonOnly: true,
        rewards: [
            { itemId: 'veil_ember', minQty: 8, maxQty: 15, chance: 1.0 },
            { itemId: 'purified_arcane_core', minQty: 4, maxQty: 8, chance: 1.0 },
            { itemId: 'void_drake_hide', minQty: 3, maxQty: 6, chance: 0.7 },
            { itemId: 'spirit_glyph', minQty: 1, maxQty: 1, chance: 0.4 },
            { itemId: 'doom_glyph', minQty: 1, maxQty: 2, chance: 0.5 },
            { itemId: 'sanguine_glyph', minQty: 1, maxQty: 2, chance: 0.45 },
            { itemId: 'void_bone', minQty: 2, maxQty: 4, chance: 0.5 },
            { itemId: 'elder_bone', minQty: 1, maxQty: 2, chance: 0.3 }
        ],
        insightChance: { eidolon: 0.25 },
        insightTiers: { eidolon: ['blazing_insight', 'veil_touched_insight'] }
    }
};

/**
 * Evolution material costs per pet type and tier
 * Tier 2 requires tier2 materials, Tier 3 requires tier3 materials
 * Uses existing items from the item registry (pet_materials, glyphs)
 * Costs are intentionally high - rare materials from Gatherer skill
 */
export const EVOLUTION_COSTS = {
    rootling: {
        tier2: { twisted_root_fiber: 50 },
        tier3: { living_arcane_husk: 100, condensed_arcane_core_fragment: 25 }
    },
    felltracker: {
        tier2: { twisted_root_fiber: 75, ashes: 200 },
        tier3: { living_arcane_husk: 150, condensed_arcane_core_fragment: 40 }
    },
    ichorebound: {
        tier2: { arcane_residue: 100, twisted_root_fiber: 100 },
        tier3: { refined_arcane_ichor: 150, purified_arcane_core: 50 }
    },
    chimera: {
        tier2: { twisted_root_fiber: 125, arcane_residue: 150 },
        tier3: { living_arcane_husk: 200, condensed_arcane_core_fragment: 75, refined_arcane_ichor: 100 }
    },
    eidolon: {
        tier2: { living_arcane_husk: 200, arcane_residue: 300 },
        tier3: { condensed_arcane_core_fragment: 150, purified_arcane_core: 100 }
    }
};

/**
 * Evolution bonuses per tier (0 = base, 1 = tier 2, 2 = tier 3)
 * Drop bonuses removed - reserved for talent tree
 * Only speed bonus increases with evolution
 */
export const EVOLUTION_BONUSES = {
    0: { dropMultiplier: 1.0, rareChance: 1.0, speedBonus: 0 },
    1: { dropMultiplier: 1.0, rareChance: 1.0, speedBonus: 0.15 },
    2: { dropMultiplier: 1.0, rareChance: 1.0, speedBonus: 0.30 }
};

/**
 * Feeding system - simple flat food totals per evolution tier
 *
 * Each evolution tier requires specific food tier:
 * - Evolution 0 (Tier 1): Basic pet food only
 * - Evolution 1 (Tier 2): Refined pet food only
 * - Evolution 2 (Tier 3): Condensed pet food only
 *
 * Higher tier pets (higher unlock level) require more food.
 * Speed bonus scales linearly with food progress toward the tier cap.
 */
export const FEED_CONFIG = {
    // Max speed bonus per evolution tier (when fully fed)
    EVOLUTION_MAX_BONUS: {
        0: 0.15,  // 15% max at tier 1
        1: 0.23,  // 23% max at tier 2
        2: 0.29   // 29% max at tier 3
    },

    // Food type per evolution tier
    EVOLUTION_FOOD_TYPE: {
        0: 'basic_pet_food',
        1: 'refined_pet_food',
        2: 'condensed_pet_food'
    },

    // Base food required per evolution tier (for lowest tier pet)
    BASE_FOOD: {
        0: 250,   // 250 basic food for tier 1
        1: 350,   // 350 refined food for tier 2
        2: 500    // 500 condensed food for tier 3
    },

    // Food multiplier per pet (higher unlock level = more food needed)
    PET_FOOD_MULTIPLIER: {
        rootling: 1.0,      // L1 pet: base amounts
        felltracker: 1.5,   // L15 pet: 1.5x food
        ichorebound: 2.0,   // L30 pet: 2x food
        chimera: 2.5,       // L50 pet: 2.5x food
        eidolon: 3.0        // L75 pet: 3x food
    }
};

/**
 * Get the speed bonus from food fed
 * @param {number} foodFed - Total food consumed by pet
 * @param {number} evolution - Pet evolution tier (0, 1, or 2)
 * @param {string} petId - Pet identifier (for food multiplier)
 * @returns {number} Speed bonus multiplier (0 to tier max)
 */
export function getFeedSpeedBonus(foodFed, evolution = 0, petId = 'rootling') {
    if (foodFed <= 0) return 0;

    const maxFood = getMaxFood(evolution, petId);
    const maxBonus = FEED_CONFIG.EVOLUTION_MAX_BONUS[evolution] ?? 0.15;

    // Linear scale: food progress * max bonus
    const progress = Math.min(foodFed / maxFood, 1);
    return progress * maxBonus;
}

/**
 * Get the maximum food for a pet based on evolution and pet type
 * @param {number} evolution - Pet evolution tier (0, 1, or 2)
 * @param {string} petId - Pet identifier (for food multiplier)
 * @returns {number} Max food for this evolution and pet
 */
export function getMaxFood(evolution = 0, petId = 'rootling') {
    const baseFood = FEED_CONFIG.BASE_FOOD[evolution] ?? 250;
    const multiplier = FEED_CONFIG.PET_FOOD_MULTIPLIER[petId] ?? 1.0;
    return Math.floor(baseFood * multiplier);
}

/**
 * Get the required food info for a pet's evolution tier
 * @param {number} evolution - Pet evolution tier (0, 1, or 2)
 * @param {string} petId - Pet identifier (for food multiplier)
 * @returns {{ foodId: string, maxFood: number }} Required food info
 */
export function getRequiredFood(evolution, petId = 'rootling') {
    const foodId = FEED_CONFIG.EVOLUTION_FOOD_TYPE[evolution] ?? 'basic_pet_food';
    const maxFood = getMaxFood(evolution, petId);
    return { foodId, maxFood };
}

/**
 * Get the list of valid pet food item IDs
 * @returns {string[]} Array of food item IDs
 */
export function getValidPetFoods() {
    return ['basic_pet_food', 'refined_pet_food', 'condensed_pet_food'];
}

/**
 * Get contracts available for a specific pet based on its role
 * @param {string} petId - Pet identifier
 * @returns {object[]} Array of available contracts
 */
export function getContractsForPet(petId) {
    const pet = PETS[petId.toUpperCase()];
    if (!pet) return [];

    const allContracts = Object.values(PET_CONTRACTS);

    // Eidolon gets ALL contracts including exclusive Veil Expeditions
    if (pet.role === 'elite') {
        return allContracts;
    }

    // Chimera gets all standard contracts but NOT Veil Expeditions
    if (pet.contractType === 'all') {
        return allContracts.filter(contract => !contract.eidolonOnly);
    }

    // Specialized pets get only their contract type
    return allContracts.filter(contract => contract.type === pet.contractType);
}

/**
 * Get pet data by ID
 * @param {string} petId - Pet identifier
 * @returns {object|null} Pet data or null
 */
export function getPetData(petId) {
    const upperPetId = petId.toUpperCase();
    return PETS[upperPetId] || null;
}

/**
 * Get evolution cost for a pet at a specific tier
 * @param {string} petId - Pet identifier
 * @param {number} currentEvolution - Current evolution (0 or 1)
 * @returns {object|null} Cost object or null
 */
export function getEvolutionCost(petId, currentEvolution) {
    const costs = EVOLUTION_COSTS[petId.toLowerCase()];
    if (!costs) return null;

    if (currentEvolution === 0) {
        return costs.tier2;
    } else if (currentEvolution === 1) {
        return costs.tier3;
    }

    return null; // Already max evolution
}

/**
 * Contract tier upgrade definitions
 * Each tier adds bonus reward items to the contract's loot pool (cumulative)
 * Unlocked via Beastmaster's Lodge I/II/III architecting milestones
 *
 * Tier 1: Beastmaster's Lodge I (Arch 40, Husb 40)
 * Tier 2: Beastmaster's Lodge II (Arch 60, Husb 60)
 * Tier 3: Beastmaster's Lodge III (Arch 80, Husb 80)
 */
export const CONTRACT_TIER_UPGRADES = {
    resource: {
        // Rootling specialty - gathering/nature focus
        1: [
            { itemId: 'softbark_logs', minQty: 3, maxQty: 8, chance: 0.45 },
            { itemId: 'corn_seed', minQty: 1, maxQty: 2, chance: 0.30 },
            { itemId: 'grimy_emberroot', minQty: 1, maxQty: 3, chance: 0.25 }
        ],
        2: [
            { itemId: 'gravewood_logs', minQty: 2, maxQty: 6, chance: 0.40 },
            { itemId: 'watermelon_seed', minQty: 1, maxQty: 2, chance: 0.25 },
            { itemId: 'grimy_stonebark', minQty: 1, maxQty: 2, chance: 0.20 }
        ],
        3: [
            { itemId: 'arcanewood_logs', minQty: 2, maxQty: 5, chance: 0.35 },
            { itemId: 'kings_fruit_seed', minQty: 1, maxQty: 1, chance: 0.15 },
            { itemId: 'grimy_crownwort', minQty: 1, maxQty: 2, chance: 0.15 }
        ]
    },
    combat: {
        // Felltracker specialty - hunting/combat focus
        1: [
            { itemId: 'raw_crimson_char', minQty: 3, maxQty: 8, chance: 0.50 },
            { itemId: 'red_wyrm_hide', minQty: 1, maxQty: 3, chance: 0.35 },
            { itemId: 'dragon_bone', minQty: 1, maxQty: 2, chance: 0.25 }
        ],
        2: [
            { itemId: 'raw_spiny_lobster', minQty: 2, maxQty: 6, chance: 0.45 },
            { itemId: 'black_drake_hide', minQty: 1, maxQty: 2, chance: 0.30 },
            { itemId: 'void_bone', minQty: 1, maxQty: 2, chance: 0.20 }
        ],
        3: [
            { itemId: 'raw_mako', minQty: 1, maxQty: 4, chance: 0.40 },
            { itemId: 'void_drake_hide', minQty: 1, maxQty: 2, chance: 0.25 },
            { itemId: 'elder_bone', minQty: 1, maxQty: 1, chance: 0.15 }
        ]
    },
    purification: {
        // Ichorebound specialty - arcane/purification focus
        1: [
            { itemId: 'refined_arcane_ichor', minQty: 2, maxQty: 5, chance: 0.50 },
            { itemId: 'clean_emberroot', minQty: 1, maxQty: 3, chance: 0.35 },
            { itemId: 'void_glyph', minQty: 1, maxQty: 1, chance: 0.15 }
        ],
        2: [
            { itemId: 'purified_arcane_core', minQty: 1, maxQty: 3, chance: 0.40 },
            { itemId: 'clean_stonebark', minQty: 1, maxQty: 2, chance: 0.30 },
            { itemId: 'binding_glyph', minQty: 1, maxQty: 1, chance: 0.12 }
        ],
        3: [
            { itemId: 'clean_crownwort', minQty: 1, maxQty: 2, chance: 0.25 },
            { itemId: 'doom_glyph', minQty: 1, maxQty: 1, chance: 0.10 },
            { itemId: 'spirit_glyph', minQty: 1, maxQty: 1, chance: 0.08 }
        ]
    },
    // Veil expeditions (Eidolon-only) get separate tier bonuses
    veil: {
        1: [
            { itemId: 'veil_ember', minQty: 1, maxQty: 2, chance: 0.40 },
            { itemId: 'void_glyph', minQty: 1, maxQty: 1, chance: 0.20 }
        ],
        2: [
            { itemId: 'veil_ember', minQty: 1, maxQty: 3, chance: 0.50 },
            { itemId: 'binding_glyph', minQty: 1, maxQty: 1, chance: 0.18 },
            { itemId: 'doom_glyph', minQty: 1, maxQty: 1, chance: 0.12 }
        ],
        3: [
            { itemId: 'veil_ember', minQty: 2, maxQty: 4, chance: 0.60 },
            { itemId: 'sanguine_glyph', minQty: 1, maxQty: 1, chance: 0.10 },
            { itemId: 'spirit_glyph', minQty: 1, maxQty: 1, chance: 0.08 }
        ]
    }
};

/**
 * Get all tier bonus rewards for a contract type at a given tier level
 * @param {string} contractType - The contract type ('resource', 'combat', 'purification', 'veil')
 * @param {number} tierLevel - Current tier level (0 = base, 1-3 = upgrades)
 * @returns {object[]} Array of bonus reward objects { itemId, minQty, maxQty, chance }
 */
export function getContractTierRewards(contractType, tierLevel = 0) {
    if (tierLevel <= 0) return [];

    const upgrades = CONTRACT_TIER_UPGRADES[contractType];
    if (!upgrades) return [];

    // Collect all rewards up to and including current tier
    const rewards = [];
    for (let tier = 1; tier <= tierLevel; tier++) {
        if (upgrades[tier]) {
            rewards.push(...upgrades[tier]);
        }
    }

    return rewards;
}

export default {
    PETS,
    PET_CONTRACTS,
    EVOLUTION_COSTS,
    EVOLUTION_BONUSES,
    FEED_CONFIG,
    getFeedSpeedBonus,
    getMaxFood,
    getRequiredFood,
    getValidPetFoods,
    getContractsForPet,
    getPetData,
    getEvolutionCost,
    HUSBANDRY_CONFIG,
    PET_CONFIG,
    CONTRACT_TIER_UPGRADES,
    getContractTierRewards
};
